import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import { db, openOrderForTable, type Order, type DiningTable } from '../lib/db'
import { tableApi, type TableLayoutItem } from '../lib/api'
import { money } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { useNow } from '../lib/hooks'
import { FloorPlan, GRID_COLS, type Placement } from '../components/FloorPlan'
import { TableGrid } from '../components/TableGrid'
import { TableBoard } from '../components/TableBoard'
import { TableEditor } from '../components/TableEditor'
import { DEFAULT_TABLE_ICON, TableIcon } from '../components/TableIcon'
import { ReserveSheet } from '../components/ReserveSheet'
import { AppBar } from '../components/AppBar'

export function TablesScreen() {
  const nav = useNavigate()
  const say = useApp((s) => s.say)
  const shop = useApp((s) => s.shop)
  const owner = isOwner(useApp((s) => s.session?.role))
  const refreshCatalog = useApp((s) => s.refreshCatalog)
  const skin = useApp((s) => s.skin)
  const now = useNow(30_000)

  const [zoneId, setZoneId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [editorFor, setEditorFor] = useState<DiningTable | null>(null)
  const [reserveFor, setReserveFor] = useState<DiningTable | null>(null)
  const [dirty, setDirty] = useState<Map<string, Placement>>(new Map())

  const zones = useLiveQuery(() => db.zones.orderBy('sortOrder').toArray(), [], [])
  const allTables = useLiveQuery(() => db.diningTables.orderBy('sortOrder').toArray(), [], [])
  /*
   * Đơn còn giữ bàn: đang mở, HOẶC đã thu tiền mà chưa giao / chưa dọn.
   *
   * Trả tiền không phải là xong việc. Ly mang về còn phải pha, bàn ăn tại chỗ còn nguyên
   * mâm khách đang ngồi. Trước đây thu tiền xong là ô bàn trắng ngay, nên người bán mất
   * dấu chính những khách đang đứng trước mặt, và bàn còn khách thì vẫn hiện là trống.
   */
  const openOrders = useLiveQuery(async () => {
    const rows = await db.orders
      .where('status')
      .anyOf('Open', 'Paid')
      .toArray()
    // Chỉ đơn trả tiền trong hôm nay. Hoá đơn của hôm qua thì khách đã về từ lâu, và
    // bản trên máy này có thể còn cũ hơn cả lúc tính năng này ra đời.
    const dayStart = new Date()
    dayStart.setHours(0, 0, 0, 0)
    const from = dayStart.toISOString()

    return rows.filter(
      (o) => o.status === 'Open' || (!o.doneAt && (o.paidAt ?? '') >= from),
    )
  }, [], [] as Order[])

  const activeZone = zoneId ?? zones[0]?.id ?? null

  // Chế độ lưới là mặc định. Quán nào chưa đụng tới cài đặt thì rơi vào đây.
  const isFloor = shop?.tableLayoutMode === 'Floor'
  const columns: 2 | 3 = shop?.gridColumns === 3 ? 3 : 2

  // Vị trí đang kéo dở đè lên vị trí đã lưu, để ô bàn không nhảy về chỗ cũ
  // trong lúc chờ máy chủ trả lời.
  const tables = useMemo(() => {
    return allTables
      .filter((t) => t.zoneId === activeZone)
      .map((t) => {
        const patch = dirty.get(t.id)
        return patch ? { ...t, ...patch } : t
      })
  }, [allTables, activeZone, dirty])

  /*
   * Sơ đồ bàn của bộ Modern không vẽ ô mang về nữa.
   *
   * Nó chưa bao giờ là một cái bàn: không ai ngồi xuống, và nó ôm cả hàng người đang đứng
   * đợi. Giờ hàng đợi đó có màn hình riêng ở mục "Mang về", nên để lại một ô câm ở đây chỉ
   * tổ làm lệch con số "mấy bàn có khách".
   *
   * Chỉ giấu lúc bán. Vào chế độ sửa sơ đồ thì ô mang về hiện lại, vì đó là chỗ duy nhất
   * chủ quán bật tắt cờ mang về.
   */
  const floorTables = useMemo(() => allTables.filter((t) => !t.isTakeaway), [allTables])
  const floorZones = useMemo(
    () => zones.filter((z) => floorTables.some((t) => t.zoneId === z.id)),
    [zones, floorTables],
  )

  /*
   * Một ô có thể đang giữ NHIỀU đơn.
   *
   * Quầy mang về là chỗ duy nhất thường xuyên như vậy: năm khách cùng đứng đợi là năm đơn
   * đã trả tiền, cùng treo ở một ô. Trước đây map chỉ giữ được một đơn nên bốn người kia
   * vô hình — nhân viên không biết còn ai chưa lấy đồ.
   *
   * Thứ tự trong danh sách quyết định ô bàn mang trạng thái nào: đơn đang gọi món đứng
   * trước (còn phải lấy tiền), rồi tới đơn đã xong đang chờ khách lấy (còn phải gọi
   * khách), cuối cùng là đơn đang làm, cũ nhất lên trước vì người đó đợi lâu nhất.
   */
  const ordersByTable = useMemo(() => {
    const map = new Map<string, Order[]>()
    for (const o of openOrders) {
      if (!o.tableId) continue
      const list = map.get(o.tableId)
      if (list) list.push(o)
      else map.set(o.tableId, [o])
    }

    const rank = (o: Order) => (o.status === 'Open' ? 0 : o.readyAt ? 1 : 2)
    for (const list of map.values()) {
      list.sort((a, b) => rank(a) - rank(b) || a.openedAt.localeCompare(b.openedAt))
    }
    return map
  }, [openOrders])

  /*
   * Bản rút gọn: mỗi bàn một đơn đại diện.
   *
   * Sơ đồ lưới và sơ đồ mặt bằng của bộ Classic chỉ vẽ được một đơn cho một ô, và đó là
   * đúng với bàn ngồi — một bàn một mâm khách. Chỉ quầy mang về mới xếp hàng, mà ở đó
   * người dùng vào khoang bên phải của bộ Modern để xử lý từng đơn.
   */
  const leadByTable = useMemo(() => {
    const map = new Map<string, Order>()
    for (const [id, list] of ordersByTable) if (list[0]) map.set(id, list[0])
    return map
  }, [ordersByTable])

  const busyCount = tables.filter((t) => (ordersByTable.get(t.id)?.length ?? 0) > 0).length
  const holding = tables.reduce(
    (sum, t) => sum + (ordersByTable.get(t.id) ?? []).reduce((n, o) => n + o.total, 0),
    0,
  )

  async function pickTable(t: DiningTable) {
    if (editing) {
      setEditorFor(t)
      return
    }
    const order = await openOrderForTable(t)
    nav(`/order/${order.id}`)
  }

  function moveTable(id: string, next: Placement) {
    setDirty((prev) => new Map(prev).set(id, next))
    void db.diningTables.update(id, next)
  }

  async function switchMode(mode: 'Grid' | 'Floor') {
    try {
      await tableApi.setMode(mode, columns)
      await refreshCatalog()
      say(mode === 'Floor' ? 'Giờ kéo thả được vị trí bàn' : 'Đã về sơ đồ lưới')
    } catch {
      say('Cần có mạng để đổi kiểu sơ đồ')
    }
  }

  /** Cỡ ô bàn lưu theo quán, không theo máy: đổi máy hay cài lại app thì vẫn còn. */
  async function setTileScale(percent: number) {
    try {
      await tableApi.setMode(isFloor ? 'Floor' : 'Grid', columns, percent)
      await refreshCatalog()
    } catch {
      say('Cần có mạng để đổi cỡ ô bàn')
    }
  }

  async function setColumns(cols: 2 | 3) {
    try {
      await tableApi.setMode('Grid', cols)
      await refreshCatalog()
    } catch {
      say('Cần có mạng để đổi số cột')
    }
  }

  /**
   * Thêm một cái bàn.
   *
   * Ghi lên máy chủ ngay chứ không để dành: máy chủ là nguồn sự thật cho sơ đồ bàn, và
   * `pullCatalog` xoá sạch bàn trên máy rồi ghi đè. Một cái bàn chỉ nằm trên máy sẽ biến
   * mất ở lần mở app kế tiếp, kéo theo đơn đang mở ở bàn đó thành đơn mồ côi.
   *
   * Mã bàn do máy này sinh và máy chủ giữ nguyên, nên bàn không nhảy mã sau khi lưu.
   */
  async function addTable() {
    const zone = activeZone
    if (!zone) return

    const used = tables.length
    const table: DiningTable = {
      id: crypto.randomUUID(),
      zoneId: zone,
      name: `Bàn ${used + 1}`,
      sortOrder: used,
      isTakeaway: false,
      icon: DEFAULT_TABLE_ICON,
      color: null,
      gridX: used % GRID_COLS,
      gridY: Math.floor(used / GRID_COLS),
      gridW: 1,
      gridH: 1,
      seats: 4,
      reservedAt: null,
      reservedName: null,
      reservedPhone: null,
      reservedPartySize: 0,
      reservedNote: null,
    }

    await db.diningTables.add(table)

    try {
      await tableApi.save({
        id: table.id,
        zoneId: table.zoneId,
        name: table.name,
        icon: table.icon,
        color: table.color,
        gridX: table.gridX,
        gridY: table.gridY,
        gridW: table.gridW,
        gridH: table.gridH,
        seats: table.seats,
        isTakeaway: table.isTakeaway,
      })
    } catch {
      // Bỏ luôn cái bàn vừa vẽ ra. Thà nói thẳng "cần có mạng" còn hơn để chủ quán xếp
      // xong cả sơ đồ rồi sáng mai mở lên thấy trống trơn.
      await db.diningTables.delete(table.id)
      say('Cần có mạng để thêm bàn')
    }
  }

  async function saveLayout() {
    const items: TableLayoutItem[] = [...dirty.entries()].map(([id, p]) => ({ id, ...p }))
    setEditing(false)

    if (items.length === 0) return

    try {
      await tableApi.saveLayout(items)
      setDirty(new Map())
      say('Đã lưu sơ đồ bàn')
    } catch {
      // Sơ đồ đã ghi vào máy rồi, mất mạng chỉ là chưa đẩy lên được.
      say('Chưa có mạng — sơ đồ đã lưu trên máy này')
    }
  }

  if (skin === 'modern' && !editing) {
    return (
      <div className="screen">
        <TableBoard
          zones={floorZones}
          tables={floorTables}
          ordersByTable={ordersByTable}
          now={now}
          owner={owner}
          floor={isFloor}
          scale={shop?.tableTileScale ?? 100}
          onScale={(v) => void setTileScale(v)}
          onArrange={() => setEditing(true)}
          onEditTable={setEditorFor}
          onReserve={setReserveFor}
        />

        {editorFor && (
          <TableEditor
            table={editorFor}
            zones={zones}
            onReserve={() => {
              setReserveFor(editorFor)
              setEditorFor(null)
            }}
            onClose={() => setEditorFor(null)}
          />
        )}

        {reserveFor && <ReserveSheet table={reserveFor} onClose={() => setReserveFor(null)} />}
      </div>
    )
  }

  return (
    <div className="screen">
      <AppBar
        title="Sơ đồ bàn"
        sub={
          busyCount > 0
            ? `${busyCount} bàn có khách · đang treo ${money(holding)}`
            : 'Tất cả bàn đang trống'
        }
        onBack={() => nav('/')}
        right={
          editing ? (
            <button type="button" className="btn btn-brand btn-sm" onClick={saveLayout}>
              Xong
            </button>
          ) : owner ? (
            <button
              type="button"
              className="icon-btn"
              onClick={() => setEditing(true)}
              aria-label="Sắp xếp bàn"
              title="Sắp xếp bàn"
            >
              ✥
            </button>
          ) : undefined
        }
      />

      <div className="body">
        <div className="tabs tabs-inline" role="tablist" aria-label="Khu vực">
          {zones.map((z) => (
            <button
              key={z.id}
              type="button"
              role="tab"
              aria-selected={z.id === activeZone}
              onClick={() => setZoneId(z.id)}
            >
              {z.name}
            </button>
          ))}
        </div>

        {editing && (
          <div className="mode-box">
            <div className="field">
              <span>Kiểu sơ đồ</span>
              <div className="seg">
                <button type="button" aria-pressed={!isFloor} onClick={() => void switchMode('Grid')}>
                  ▦ Lưới đều
                </button>
                <button type="button" aria-pressed={isFloor} onClick={() => void switchMode('Floor')}>
                  ✥ Tự xếp
                </button>
              </div>
            </div>

            {isFloor ? (
              <p className="note">
                Kéo ô bàn tới đúng chỗ trên mặt bằng thật. Dùng phím mũi tên cũng dời được. Chạm
                vào bàn để đổi tên, biểu tượng, màu hoặc đặt bàn trước.
              </p>
            ) : (
              <>
                <div className="field">
                  <span>Số cột</span>
                  <div className="seg">
                    {([2, 3] as const).map((c) => (
                      <button
                        key={c}
                        type="button"
                        aria-pressed={columns === c}
                        onClick={() => void setColumns(c)}
                      >
                        {c} cột
                      </button>
                    ))}
                  </div>
                </div>
                <p className="note">
                  Bàn xếp đều theo thứ tự, không cần vẽ vị trí. Chạm vào bàn để đổi tên, biểu
                  tượng, màu hoặc đặt bàn trước.
                </p>
              </>
            )}
          </div>
        )}

        {tables.length === 0 ? (
          <div className="empty">
            <TableIcon className="empty-icon" />
            <p>Khu vực này chưa có bàn nào.</p>
            {owner && (
              <button type="button" className="btn btn-brand" onClick={() => setEditing(true)}>
                Thêm bàn
              </button>
            )}
          </div>
        ) : isFloor ? (
          <FloorPlan
            tables={tables}
            openOrders={leadByTable}
            now={now}
            editing={editing}
            selectedId={editorFor?.id}
            onPick={pickTable}
            onMove={moveTable}
          />
        ) : (
          <TableGrid
            tables={tables}
            openOrders={leadByTable}
            now={now}
            columns={columns}
            editing={editing}
            selectedId={editorFor?.id}
            onPick={pickTable}
          />
        )}
      </div>

      {editorFor && (
        <TableEditor
          table={editorFor}
          zones={zones}
          onReserve={() => {
            setReserveFor(editorFor)
            setEditorFor(null)
          }}
          onClose={() => setEditorFor(null)}
        />
      )}

      {reserveFor && <ReserveSheet table={reserveFor} onClose={() => setReserveFor(null)} />}

      {editing && (
        <div className="dock">
          <button type="button" className="btn btn-ghost" onClick={() => void addTable()}>
            + Thêm bàn
          </button>
          <button type="button" className="btn btn-brand grow" onClick={saveLayout}>
            Xong
          </button>
        </div>
      )}
    </div>
  )
}
